import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable } from '@angular/core';
import { currentTheme, refreshTheme } from 'devextreme/viz/themes';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';

type SupportedTheme = 'light' | 'dark' | 'system';
type EffectiveTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class Theme {
  private readonly document = inject(DOCUMENT);

  readonly userTheme = injectLocalStorage<SupportedTheme>('firmfirst-theme', {
    defaultValue: 'system',
  });

  readonly effectiveTheme = computed<EffectiveTheme>(() => {
    const pref = this.userTheme();
    if (pref === 'system') {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return pref;
  });

  readonly isDark = computed(() => this.effectiveTheme() === 'dark');

  constructor() {
    effect(() => this.applyTheme());

    globalThis.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (this.userTheme() === 'system') {
        this.userTheme.set('system');
      }
    });
  }

  private applyTheme() {
    const isDark = this.isDark();

    // Toggle html.theme-dark for variables.scss custom properties
    this.document.documentElement.classList.toggle('theme-dark', isDark);

    // Update DevExtreme viz theme (charts, gauges, etc.) — SVG colors come from
    // the viz theme registry, not from CSS, so this must be set explicitly.
    currentTheme(isDark ? 'fluent.blue.dark' : 'fluent.blue.light');
    refreshTheme();

    const activeName = isDark ? 'theme-dark' : 'theme-light';
    const inactiveName = isDark ? 'theme-light' : 'theme-dark';

    // Disable the inactive theme link
    const inactiveLink = this.document.head.querySelector<HTMLLinkElement>(
      `link[href*="${inactiveName}"]`,
    );
    if (inactiveLink) inactiveLink.disabled = true;

    // Enable the active theme link and move it to the end of <head> so it wins
    // the cascade over any :root { } variables DevExtreme injects as <style> tags at runtime
    const activeLink = this.document.head.querySelector<HTMLLinkElement>(
      `link[href*="${activeName}"]`,
    );
    if (activeLink) {
      activeLink.disabled = false;
      this.document.head.appendChild(activeLink);
    }
  }
}
